import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Check, Loader2, Clock, AlertCircle } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 15;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, attempts: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, attempts: 0 });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.payment_status === "authorized") {
          setState({ status: "success", data, attempts });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "cancelled") {
          setState({ status: "failed", data, attempts });
          return;
        }
      } catch {
        // 404 during first tick before webhook — keep polling
      }
      if (attempts >= MAX_POLLS) {
        setState((s) => ({ ...s, status: "timeout", attempts }));
        return;
      }
      setState((s) => ({ ...s, attempts }));
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-16">
        <div className="border rounded-3xl bg-card p-8 text-center" data-testid="payment-result">
          {state.status === "polling" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[color:hsl(var(--teal))] animate-spin" />
              <h1 className="mt-4 font-display font-bold text-2xl">Confirming your payment…</h1>
              <p className="mt-2 text-sm text-muted-foreground">Talking to Stripe. This usually takes a second.</p>
            </>
          )}
          {state.status === "success" && (
            <>
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 grid place-items-center">
                <Check className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h1 className="mt-4 font-display font-bold text-2xl" data-testid="payment-success-title">
                {state.data?.kind === "immediate_help"
                  ? "Authorization confirmed"
                  : state.data?.kind === "helper_pro"
                  ? "Helper Pro is active"
                  : "Thanks — payment received"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {state.data?.kind === "immediate_help"
                  ? "Your card is held. Funds will be captured when the session ends."
                  : state.data?.kind === "helper_pro"
                  ? "Your reduced Immediate Help fee kicks in on your very next session."
                  : `We processed $${state.data?.amount?.toFixed(2)}. Your helper has been notified.`}
              </p>
              <div className="mt-6 flex gap-2 justify-center">
                <Link to="/home" className="rounded-full px-4 py-2 text-sm border hover:bg-secondary" data-testid="return-home">Back to Home</Link>
                {state.data?.kind === "helper_pro" && (
                  <Link to="/earnings" className="rounded-full px-4 py-2 text-sm bg-[color:hsl(var(--teal))] text-white hover:opacity-90">See earnings</Link>
                )}
              </div>
            </>
          )}
          {state.status === "timeout" && (
            <>
              <Clock className="w-10 h-10 mx-auto text-[color:hsl(var(--amber))]" />
              <h1 className="mt-4 font-display font-bold text-2xl">Still processing…</h1>
              <p className="mt-2 text-sm text-muted-foreground">Stripe is taking longer than usual. Refresh in a minute — your payment status will update automatically.</p>
            </>
          )}
          {(state.status === "failed" || state.status === "error") && (
            <>
              <AlertCircle className="w-10 h-10 mx-auto text-red-500" />
              <h1 className="mt-4 font-display font-bold text-2xl">Payment didn't go through</h1>
              <p className="mt-2 text-sm text-muted-foreground">No worries — nothing was charged. Try again or ask us for help.</p>
              <Link to="/home" className="mt-6 inline-block rounded-full px-4 py-2 text-sm border hover:bg-secondary">Back to Home</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
